/**
 * Raiz do SPA da Plataforma (Task 10): router + React Query + guard de staff.
 *
 * Todas as dependências de I/O (cliente de API, provedor de sessão, cache de
 * query) entram por props — o entrypoint (`main.ts`) as monta a partir do
 * ambiente, e o teste pode montar as suas. O componente em si não lê env.
 */
import { createElement as h, type ReactElement } from "react";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { BrowserRouter, MemoryRouter, Route, Routes } from "react-router-dom";
import { ApiClientProvider } from "./api/ApiClientContext.js";
import { StaffSessionBoundary } from "./auth/StaffSessionBoundary.js";
import { AppShell } from "./layout/AppShell.js";
import { StatusPanel } from "./components/StatusPanel.js";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary.js";
import { DashboardExecutivo } from "./pages/DashboardExecutivo.js";
import { DashboardOperacional } from "./pages/DashboardOperacional.js";
import { ResumoExecutivo } from "./pages/ResumoExecutivo.js";
import { Canais } from "./pages/Canais.js";
import { CanalVenda, MercadoLivre } from "./pages/MercadoLivre.js";
import { Televendas } from "./pages/Televendas.js";
import { Atendimento } from "./pages/Atendimento.js";
import { Aplicacoes } from "./pages/Aplicacoes.js";
import { Categorias } from "./pages/Categorias.js";
import { Clientes } from "./pages/Clientes.js";
import { EstoqueFiliais } from "./pages/EstoqueFiliais.js";
import { Notificacoes } from "./pages/Notificacoes.js";
import { Auditoria } from "./pages/Auditoria.js";
import { IntegracaoDetalhe, Integracoes } from "./pages/Integracoes.js";
import { Pedidos } from "./pages/Pedidos.js";
import { PedidoDetalhe } from "./pages/PedidoDetalhe.js";
import { Expedicao } from "./pages/Expedicao.js";
import { ExpedicaoDetalhe } from "./pages/ExpedicaoDetalhe.js";
import { Rastreio } from "./pages/Rastreio.js";
import { Margens } from "./pages/Margens.js";
import { Estoque } from "./pages/Estoque.js";
import { Crm360 } from "./pages/Crm360.js";
import { Pipeline } from "./pages/Pipeline.js";
import { Orcamentos } from "./pages/Orcamentos.js";
import { Tarefas } from "./pages/Tarefas.js";
import { Segmentos } from "./pages/Segmentos.js";
import { RespostasRapidas } from "./pages/RespostasRapidas.js";
import { Grupos } from "./pages/Grupos.js";
import { Fluxos } from "./pages/Fluxos.js";
import { LeadScore } from "./pages/LeadScore.js";
import { Sincronizacao } from "./pages/Sincronizacao.js";
import { Configuracoes } from "./pages/Configuracoes.js";
import { ConfiguracoesIA } from "./pages/ConfiguracoesIA.js";
import { Usuarios } from "./pages/Usuarios.js";
import { AccessProvider, RouteGuard } from "./auth/access.js";
import { MarketingFaixaPresale } from "./pages/MarketingFaixaPresale.js";
import { MarketingBanners } from "./pages/MarketingBanners.js";
import { Cupons } from "./pages/Cupons.js";
import { Rma } from "./pages/Rma.js";
import { RmaDetalhe } from "./pages/RmaDetalhe.js";
import { Conciliacao } from "./pages/Conciliacao.js";
import { ConciliacaoDetalhe } from "./pages/ConciliacaoDetalhe.js";
import { ContasGerenciais } from "./pages/ContasGerenciais.js";
import { ContasPagar } from "./pages/ContasPagar.js";
import { ContasReceber } from "./pages/ContasReceber.js";
import { FluxoCaixa } from "./pages/FluxoCaixa.js";
import { Rentabilidade } from "./pages/Rentabilidade.js";
import { CatalogoProdutos } from "./pages/CatalogoProdutos.js";
import { ProdutoDetalhe } from "./pages/ProdutoDetalhe.js";
import { Disponibilidade } from "./pages/Disponibilidade.js";
import { Divergencias } from "./pages/Divergencias.js";
import type { PlatformApiClient } from "./api/platform-client.js";
import type { StaffSessionProvider } from "./auth/session.js";

/** Rotas entregues pelas Tasks 10–13. A doc viva (GEN:apps) deriva as telas de `src/pages/`. */
export const PLATFORM_ROUTES: Array<{ path: string; element: () => ReactElement }> = [
  { path: "/", element: DashboardExecutivo },
  { path: "/operacional", element: DashboardOperacional },
  // Redesign admin, Fase 2: resumo de uma tela para a diretoria.
  { path: "/resumo", element: ResumoExecutivo },
  { path: "/pedidos", element: Pedidos },
  // Redesign admin, Fase 3: canais de venda e quadro do Mercado Livre.
  { path: "/televendas", element: Televendas },
  { path: "/canais", element: Canais },
  { path: "/canais/mercado-livre", element: MercadoLivre },
  { path: "/canais/:canal", element: CanalVenda },
  // Task 12 (gestão centralizada de pedidos): o detalhe reúne pagamento,
  // expedição, transporte, faturamento fiscal, cancelamento/devolução e a
  // linha do tempo de estados do MESMO registro, com a origem navegável.
  { path: "/pedidos/:orderId", element: PedidoDetalhe },
  { path: "/margens", element: Margens },
  { path: "/estoque", element: Estoque },
  // Redesign admin, Fase 4: categorias, aplicações, estoque por filial, lista de
  // clientes (o 360 segue em /clientes/:customerId) e central de notificações.
  { path: "/estoque/filiais", element: EstoqueFiliais },
  { path: "/categorias", element: Categorias },
  { path: "/aplicacoes", element: Aplicacoes },
  { path: "/clientes", element: Clientes },
  { path: "/notificacoes", element: Notificacoes },
  // Redesign admin, Fase 5: integrações (visão e detalhe), auditoria. Configurações
  // segue em /configuracoes.
  { path: "/integracoes", element: Integracoes },
  { path: "/integracoes/:key", element: IntegracaoDetalhe },
  { path: "/auditoria", element: Auditoria },
  // Redesign admin: caixa de entrada em 3 painéis (Chatwoot). `?kpi=` mantém o drill-down de indicador.
  { path: "/inbox", element: Atendimento },
  // Telas migradas do shell de fixture para dado real (Task 10): detalhe de
  // conversa, Customer 360 e composição do lead score.
  { path: "/inbox/grupo/:groupId", element: Atendimento },
  { path: "/inbox/:conversationId", element: Atendimento },
  // Task 13 (CRM 360): `/clientes/:customerId` é a ÚNICA visão 360 do cliente.
  // Ela substituiu a visão parcial da Task 10 (`pages/Customer360.ts`, removido)
  // em vez de ganhar uma rota paralela: duas telas de 360 mostrariam dois
  // totais para o mesmo cliente (D-13.1). Todos os links existentes (inbox,
  // detalhe de conversa, pedidos) continuam apontando para cá.
  { path: "/clientes/:customerId", element: Crm360 },
  { path: "/clientes/:customerId/lead-score", element: LeadScore },
  // Demais telas da Task 13: funil, propostas, fila de follow-up e segmentos.
  { path: "/crm/pipeline", element: Pipeline },
  { path: "/crm/orcamentos", element: Orcamentos },
  { path: "/crm/tarefas", element: Tarefas },
  { path: "/crm/segmentos", element: Segmentos },
  { path: "/crm/respostas-rapidas", element: RespostasRapidas },
  { path: "/crm/grupos", element: Grupos },
  { path: "/crm/fluxos", element: Fluxos },
  { path: "/sincronizacao", element: Sincronizacao },
  // Task 15 (assistência técnica/garantia/RMA): fila de casos (`/garantias`,
  // também o destino do drill-down do KPI "garantias abertas") e detalhe com a
  // máquina de estados completa, prova, responsável/prazo, resolução e a linha
  // do tempo auditável por etapa.
  { path: "/garantias", element: Rma },
  { path: "/garantias/:caseId", element: RmaDetalhe },
  // Task 14 (logística e expedição): fila de separação→despacho, detalhe com
  // conferência/embalagem/ocorrências/reversa e rastreio unificado (estado
  // interno + transportador).
  { path: "/expedicao", element: Expedicao },
  { path: "/expedicao/:taskId", element: ExpedicaoDetalhe },
  { path: "/rastreio", element: Rastreio },
  // Task 11 (catálogo/estoque): lista do catálogo em duas camadas, detalhe do
  // produto, disponibilidade/reservas e divergências Bling↔local item a item.
  { path: "/catalogo", element: CatalogoProdutos },
  { path: "/catalogo/:productId", element: ProdutoDetalhe },
  { path: "/disponibilidade", element: Disponibilidade },
  { path: "/divergencias", element: Divergencias },
  // Task 16 (financeiro gerencial e inteligência comercial): conciliação de
  // pagamentos (lista + detalhe navegável até o pedido), contas/lançamentos
  // gerenciais e rentabilidade com margem realizada por pedido/produto e
  // drill-down até os lançamentos, a conciliação e o pedido/produto de origem.
  // Gerencial, nunca fiscal; nunca executa pagamento.
  { path: "/financeiro/conciliacao", element: Conciliacao },
  { path: "/financeiro/conciliacao/:reconciliationId", element: ConciliacaoDetalhe },
  { path: "/financeiro/contas", element: ContasGerenciais },
  { path: "/financeiro/fluxo-de-caixa", element: FluxoCaixa },
  { path: "/financeiro/contas-a-receber", element: ContasReceber },
  { path: "/financeiro/contas-a-pagar", element: ContasPagar },
  { path: "/financeiro/rentabilidade", element: Rentabilidade },
  // Entrega 0 (R3–R5): configuração parametrizável das integrações — tela
  // read-only sobre o registry de packages/config (estado simulador/real, o
  // que falta e o flag), sem jamais exibir o valor de um segredo.
  { path: "/configuracoes", element: Configuracoes },
  // Usuários da equipe (0058): CRUD, bloqueio rápido e permissão por módulo; só administrador.
  { path: "/configuracoes/usuarios", element: Usuarios },
  // Assistente de IA: chaves, modelos, porteiro, tom, piloto e aprendizado. Só o dono da IA.
  { path: "/configuracoes/ia", element: ConfiguracoesIA },
  // Plano Loja/Twotrees, Fase 8: faixa de pré-venda acima do header da Loja —
  // toggle master + destaques (produto + texto), refletindo sem redeploy.
  { path: "/marketing/faixa-presale", element: MarketingFaixaPresale },
  // Plano Loja/Twotrees, Fase 9: banners promocionais da home — toggle master
  // + cadastro (imagem + link opcional), refletindo sem redeploy.
  { path: "/marketing/banners", element: MarketingBanners },
  // Plano Loja/Twotrees, Fase 10: cupons de desconto do checkout da Loja.
  { path: "/cupons", element: Cupons }
];

export function AppRoutes(): ReactElement {
  return h(
    Routes,
    null,
    ...PLATFORM_ROUTES.map((route) => h(Route, { key: route.path, path: route.path, element: h(route.element) })),
    h(Route, {
      key: "*",
      path: "*",
      element: h(StatusPanel, { kind: "empty", title: "Rota não encontrada nesta versão da Plataforma" })
    })
  );
}

export interface AppProps {
  client: PlatformApiClient;
  sessionProvider: StaffSessionProvider;
  queryClient: QueryClient;
  /** `memory` permite montar o app em teste/SSR sem histórico de browser. */
  routerMode?: "browser" | "memory";
  initialPath?: string;
}

export function App({ client, sessionProvider, queryClient, routerMode = "browser", initialPath = "/" }: AppProps): ReactElement {
  const router = routerMode === "memory" ? MemoryRouter : BrowserRouter;
  const routerProps = routerMode === "memory" ? { initialEntries: [initialPath] } : {};
  // `children` vai dentro do objeto de props (e não como argumento variádico)
  // porque os componentes deste app declaram `children` obrigatório — assim o
  // typecheck cobre a composição da árvore.
  return h(
    router,
    routerProps,
    h(QueryClientProvider, {
      client: queryClient,
      children: h(ApiClientProvider, {
        client,
        children: h(StaffSessionBoundary, {
          provider: sessionProvider,
          children: h(AccessProvider, { children: h(AppShell, { children: h(RouteGuard, { children: h(RouteErrorBoundary, { children: h(AppRoutes) }) }) }) })
        })
      })
    })
  );
}
